<template>
    <div class="exports-status">
        <PageBackWithLink :to="{ name: 'developer' }" :text="$t('global.back')"/>
        <PageHeader :title="$t('exports-status.title')" :subtitle="$t('exports-status.subtitle')"/>

        <p v-if="loadError" class="exports-status__error">
            <TextSimple color="red">{{ $t('exports-status.error.load') }}</TextSimple>
        </p>

        <GridContainer>
            <GridCard>
                <GridContent>
                    <GridHeader :title="$t('exports-status.queue.title')"/>
                    <template v-if="status.queue">
                        <div class="exports-status__queue">
                            <div class="exports-status__metric">
                                <TextTitle size="l">{{ status.queue.visible }}</TextTitle>
                                <TextSimple size="s" color="grey">{{ $t('exports-status.queue.visible') }}</TextSimple>
                            </div>
                            <div class="exports-status__metric">
                                <TextTitle size="l">{{ status.queue.inFlight }}</TextTitle>
                                <TextSimple size="s" color="grey">{{ $t('exports-status.queue.inFlight') }}</TextSimple>
                            </div>
                            <div class="exports-status__metric" :class="{ 'is-alert': status.queue.dlq > 0 }">
                                <TextTitle size="l">{{ status.queue.dlq }}</TextTitle>
                                <TextSimple size="s" color="grey">{{ $t('exports-status.queue.dlq') }}</TextSimple>
                            </div>
                        </div>
                    </template>
                    <TextSimple v-else color="red">{{ $t('exports-status.queue.unavailable') }}</TextSimple>
                </GridContent>
            </GridCard>

            <template v-slot:bottom>
                <GridCard>
                    <GridContent>
                        <GridHeader :title="$t('exports-status.processing.title')"/>
                        <TextSimple v-if="status.processing.length === 0" color="grey">
                            {{ $t('exports-status.processing.empty') }}
                        </TextSimple>
                        <TableSticky v-else :fields="processingFields" :data="processingRows"/>
                    </GridContent>
                </GridCard>

                <GridCard>
                    <GridContent>
                        <GridHeader :title="$t('exports-status.requested.title')"/>
                        <TextSimple v-if="status.requested.length === 0" color="grey">
                            {{ $t('exports-status.requested.empty') }}
                        </TextSimple>
                        <TableSticky v-else :fields="requestedFields" :data="requestedRows"/>
                    </GridContent>
                </GridCard>

                <GridCard>
                    <GridContent>
                        <GridHeader :title="$t('exports-status.completed.title')"/>
                        <TextSimple v-if="status.recentCompleted.length === 0" color="grey">
                            {{ $t('exports-status.completed.empty') }}
                        </TextSimple>
                        <TableSticky v-else :fields="completedFields" :data="completedRows"/>
                    </GridContent>
                </GridCard>

                <GridCard>
                    <GridContent>
                        <GridHeader :title="$t('exports-status.failed.title')"/>
                        <TextSimple v-if="status.recentFailed.length === 0" color="grey">
                            {{ $t('exports-status.failed.empty') }}
                        </TextSimple>
                        <TableSticky v-else :fields="failedFields" :data="failedRows"/>
                    </GridContent>
                </GridCard>
            </template>
        </GridContainer>
    </div>
</template>

<script>
import API from '@/api/api-admin';
import listRoutes from '@/api/list-routes';
import moment from 'moment';
import PageBackWithLink from '@/components/ui/page/PageBackWithLink';
import PageHeader from '@/components/ui/page/PageHeader';
import GridContainer from '@/components/ui/grid/GridContainer';
import GridCard from '@/components/ui/grid/GridCard';
import GridContent from '@/components/ui/grid/GridContent';
import GridHeader from '@/components/ui/grid/GridHeader';
import TableSticky from '@/components/ui/table-sticky/TableSticky';
import TextSimple from '@/components/ui/text/TextSimple';
import TextTitle from '@/components/ui/text/TextTitle';

const REFRESH_INTERVAL_MS = 15000;

const filtersColumn = { key: 'filtersLabel', type: 'text', translation: 'exports-status.columns.filters', size: 'xl' };
const requestedAtColumn = { key: 'dateRequestedFormatted', type: 'text', translation: 'exports-status.columns.requestedAt', size: 'm' };
const waitColumn = { key: 'waitLabel', type: 'text', translation: 'exports-status.columns.wait', size: 's' };

export default {
    name: 'exportsStatusPage',
    components: {
        PageBackWithLink,
        PageHeader,
        GridContainer,
        GridCard,
        GridContent,
        GridHeader,
        TableSticky,
        TextSimple,
        TextTitle,
    },
    data() {
        return {
            status: {
                queue: null,
                processing: [],
                requested: [],
                recentCompleted: [],
                recentFailed: [],
            },
            loadError: false,
            now: Date.now(),
            timer: null,
            processingFields: [
                { key: 'routeName', type: 'text', translation: 'exports-status.columns.type', size: 'l' },
                { key: 'requestedBy', type: 'text', translation: 'exports-status.columns.requestedBy', size: 'm' },
                filtersColumn,
                requestedAtColumn,
                waitColumn,
                { key: 'dateProcessingStartFormatted', type: 'text', translation: 'exports-status.columns.startedAt', size: 'm' },
                { key: 'ageLabel', type: 'text', translation: 'exports-status.columns.age', size: 's' },
            ],
            requestedFields: [
                { key: 'position', type: 'text', translation: 'exports-status.columns.position', size: 'xs' },
                { key: 'routeName', type: 'text', translation: 'exports-status.columns.type', size: 'l' },
                { key: 'requestedBy', type: 'text', translation: 'exports-status.columns.requestedBy', size: 'm' },
                filtersColumn,
                requestedAtColumn,
                waitColumn,
            ],
            completedFields: [
                { key: 'routeName', type: 'text', translation: 'exports-status.columns.type', size: 'l' },
                { key: 'requestedBy', type: 'text', translation: 'exports-status.columns.requestedBy', size: 'm' },
                filtersColumn,
                requestedAtColumn,
                waitColumn,
                { key: 'dateEmailSentFormatted', type: 'text', translation: 'exports-status.columns.emailSentAt', size: 'm' },
                { key: 's3Url', type: 'url', translation: 'exports-status.columns.fileLink', size: 'l' },
                { key: 'rowCount', type: 'text', translation: 'exports-status.columns.rowCount', size: 's' },
            ],
            failedFields: [
                { key: 'routeName', type: 'text', translation: 'exports-status.columns.type', size: 'l' },
                { key: 'requestedBy', type: 'text', translation: 'exports-status.columns.requestedBy', size: 'm' },
                filtersColumn,
                requestedAtColumn,
                waitColumn,
                { key: 'dateProcessingEndFormatted', type: 'text', translation: 'exports-status.columns.endedAt', size: 'm' },
                { key: 'errorCode', type: 'text', translation: 'exports-status.columns.errorCode', size: 's' },
                { key: 'errorMessage', type: 'text', translation: 'exports-status.columns.errorMessage', size: 'xl' },
            ],
        };
    },
    computed: {
        processingRows() {
            return this.mapWithFilters(this.status.processing, (item) => ({
                ...this.withTiming(item),
                dateProcessingStartFormatted: this.formatDate(item.dateProcessingStart),
                ageLabel: this.ageInMinutes(item.dateProcessingStart),
            }));
        },
        requestedRows() {
            return this.mapWithFilters(this.status.requested, (item) => this.withTiming(item));
        },
        completedRows() {
            return this.mapWithFilters(this.status.recentCompleted, (item) => ({
                ...this.withTiming(item),
                dateEmailSentFormatted: this.formatDate(item.dateEmailSent),
            }));
        },
        failedRows() {
            return this.mapWithFilters(this.status.recentFailed, (item) => ({
                ...this.withTiming(item),
                dateProcessingEndFormatted: this.formatDate(item.dateProcessingEnd),
            }));
        },
    },
    mounted() {
        this.load();
        this.timer = setInterval(this.load, REFRESH_INTERVAL_MS);
    },
    beforeDestroy() {
        clearInterval(this.timer);
    },
    methods: {
        async load() {
            try {
                const { data } = await API.get(listRoutes.exportsQueue, { skipGlobalErrorHandler: true });
                this.status = {
                    queue: data.queue ?? null,
                    processing: data.processing ?? [],
                    requested: data.requested ?? [],
                    recentCompleted: data.recentCompleted ?? data.completedLastHour ?? [],
                    recentFailed: data.recentFailed ?? data.failedLastHour ?? [],
                };
                this.loadError = false;
                this.now = Date.now();
            } catch (error) {
                this.loadError = true;
            }
        },
        mapWithFilters(items, mapper) {
            return items.map((item) => ({
                ...mapper(item),
                filtersLabel: this.formatFilters(item.filtersApplied),
            }));
        },
        formatFilters(filtersApplied) {
            if (!filtersApplied) {
                return '-';
            }
            const list = Array.isArray(filtersApplied.filters) ? filtersApplied.filters : null;
            if (list === null) {
                return JSON.stringify(filtersApplied);
            }
            if (list.length === 0) {
                return '-';
            }
            return list
                .map((filter) => {
                    const field = filter.field ?? '';
                    const operator = filter.operator ?? '';
                    const value = filter.formatted_value ?? filter.value ?? '';
                    return [field, operator, value].filter(Boolean).join(' ');
                })
                .join(' · ');
        },
        withTiming(item) {
            return {
                ...item,
                dateRequestedFormatted: this.formatDate(item.dateRequested),
                waitLabel: this.waitLabel(item),
            };
        },
        formatDate(value) {
            return value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '-';
        },
        waitLabel(item) {
            return this.formatWait(item.dateRequested, item.dateProcessingStart || item.dateProcessingEnd || null);
        },
        formatWait(startIso, endIso) {
            if (!startIso) {
                return '-';
            }
            const endMs = endIso ? new Date(endIso).getTime() : this.now;
            const minutes = Math.max(0, Math.floor((endMs - new Date(startIso).getTime()) / 60000));
            if (!Number.isFinite(minutes)) {
                return '-';
            }
            const hours = Math.floor(minutes / 60);
            const rest = minutes % 60;
            if (hours === 0) {
                return `${minutes} min`;
            }
            if (rest === 0) {
                return `${hours} h`;
            }
            return `${hours} h ${rest} min`;
        },
        ageInMinutes(value) {
            if (!value) {
                return '-';
            }
            return `${Math.floor((this.now - new Date(value).getTime()) / 60000)} min`;
        },
    },
};
</script>

<style scoped lang="scss">
.exports-status__error {
    margin-bottom: 1rem;
}

.exports-status__queue {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
}

.exports-status__metric {
    min-width: 10rem;
}

.exports-status__metric.is-alert {
    color: var(--color-text-red);
}
</style>
